/**
 * Transformadores de dados para o processo de salvamento
 * 
 * Funções puras para transformar e normalizar dados
 * Origem: linhas 294-368 do useSaveController.ts
 */

import { storage } from '../../../lib/storage/storage';
import { Keys, readWithAliases } from '../../../lib/storage/keys';
import { DEFAULT_TIME } from '../constants/saveConstants';

/**
 * Garante que saveData possui caminho_arquivo completo
 * 
 * Lógica:
 * 1. Se fluxo TEMP e sem caminho, tenta construir
 * 2. Tenta recuperar do storage
 * 3. Tenta construir usando buildFilePath
 * 4. Persiste no storage
 * 
 * @param data - Dados do salvamento
 * @param savedFilePathRef - Ref com caminho salvo pelo Corel
 * @param buildFilePath - Função para construir caminho
 * @param role - Role do usuário
 * @returns Dados com caminho_arquivo garantido
 */
export function ensureCaminhoArquivo(
  data: any,
  savedFilePathRef: string,
  buildFilePath?: (data: any, role: string, options?: any) => { filePath: string } | null,
  role?: string
): any {
  try {
    // Fluxo TEMP: construir caminho se ausente
    if (data?.fluxo?.tipo === 'temp') {
      if (!data.caminho_arquivo) {
        if (savedFilePathRef) {
          data.caminho_arquivo = savedFilePathRef;
        } else {
          const dirRaw = data?.diretorios?.interno || data?.diretorios?.externo || '';
          const nameRaw = data?.nome_arquivo || '';
          if (dirRaw && nameRaw) {
            const normalizedDir = dirRaw.endsWith('\\') ? dirRaw : `${dirRaw}\\`;
            data.caminho_arquivo = `${normalizedDir}${nameRaw}`;
          }
        }
      }
      
      // Atualizar ref se caminho foi definido
      if (typeof data.caminho_arquivo === 'string' && data.caminho_arquivo) {
        // Note: savedFilePathRef é passado por valor, então não podemos atualizá-lo aqui
        // O caller precisa fazer: savedFilePathRef.current = data.caminho_arquivo
      }
      
      return data;
    }
    
    // Fluxo NORMAL
    if (data && !data.caminho_arquivo) {
      // 1. Tentar recuperar do storage
      try {
        const persisted = readWithAliases<any>(
          Keys.export.currentSaveData,
          ['currentSaveData'],
          { area: 'session', persistIfAliased: true }
        );
        if (persisted?.caminho_arquivo && 
            typeof persisted.caminho_arquivo === 'string' && 
            persisted.caminho_arquivo.includes('\\')) {
          data.caminho_arquivo = persisted.caminho_arquivo;
        }
      } catch {}
      
      // 2. Tentar construir usando buildFilePath
      if (!data.caminho_arquivo && buildFilePath && role) {
        try {
          const roleStr = role.toString();
          const built = buildFilePath(data, roleStr, { useCVIfVariavel: false });
          if (built?.filePath) {
            data.caminho_arquivo = built.filePath;
          }
        } catch {}
      }
      
      // 3. Persistir no storage
      try {
        storage.set(Keys.export.currentSaveData, data, { 
          area: 'session', 
          ttlMs: 24 * 60 * 60 * 1000 
        });
      } catch {}
    }
  } catch {}
  
  return data;
}

/**
 * Normaliza string de horário para formato HH:mm
 * 
 * @param timeStr - String de horário (ex: "18:0", "9:30", "18:00")
 * @returns Horário normalizado (ex: "18:00", "09:30")
 * 
 * @example
 * normalizeToHHmm("18:0")   // "18:00"
 * normalizeToHHmm("9:30")   // "09:30"
 * normalizeToHHmm("invalid") // "18:00" (default)
 */
export function normalizeToHHmm(timeStr: string): string {
  if (!timeStr || typeof timeStr !== 'string') return DEFAULT_TIME;
  
  const parts = timeStr.split(':');
  if (parts.length >= 2) {
    const hh = parts[0].padStart(2, '0');
    const mm = parts[1].padStart(2, '0');
    return `${hh}:${mm}`;
  }
  
  return timeStr;
}

/**
 * Deriva objetos estruturados a partir de workingData
 * 
 * Usado quando não há metadados disponíveis do Corel
 * 
 * @param data - Dados do salvamento
 * @returns Array de objetos estruturados
 */
export function deriveObjectsFromWorkingData(data: any): any[] {
  if (!Array.isArray(data?.objetos)) return [];
  
  return data.objetos.map((obj: any, index: number) => ({
    staticId: obj.staticId || obj.id || `obj-${index + 1}`,
    guid: obj.guid ?? null,
    nome: obj.nome || `Objeto ${index + 1}`,
    baseName: obj.baseName || obj.nome || `Objeto ${index + 1}`,
    multiplicador: obj.multiplicador ?? obj.quantity ?? 1,
    contracao: obj.contracao ?? obj.contraction ?? null,
    exportIndex: obj.exportIndex ?? null,
    largura: obj.largura ?? obj.width ?? null,
    altura: obj.altura ?? obj.height ?? null,
    tipo: obj.tipo ?? null,
    layer: obj.layer ?? null,
    page: null,
  }));
}

/**
 * Calcula horário de entrega baseado na opção selecionada
 * 
 * @param entregaOpcao - Opção de entrega (HOJE, AMANHÃ, NOTURNA, URGENTE)
 * @param horarioCorte - Horário de corte (padrão: "18:00")
 * @returns Horário de entrega no formato HH:mm
 */
export function calcularHorarioEntrega(
  entregaOpcao: string,
  horarioCorte: string = DEFAULT_TIME
): string {
  const entregaUpper = String(entregaOpcao).toUpperCase();
  
  switch (entregaUpper) {
    case 'HOJE':
      return horarioCorte;
    case 'AMANHÃ':
    case 'AMANHA':
      return '09:00';
    case 'NOTURNA':
      return '22:00';
    case 'URGENTE':
      return '12:00';
    default:
      return DEFAULT_TIME;
  }
}

/**
 * Calcula previsão de entrega para mensagem ao cliente
 * 
 * @param entregaOpcao - Opção de entrega
 * @param horarioCorte - Horário de corte
 * @returns String de previsão formatada
 */
export function calcularPrevisaoEntrega(
  entregaOpcao: string,
  horarioCorte: string = DEFAULT_TIME
): string {
  const entregaUpper = String(entregaOpcao).toUpperCase();
  
  // Parse horário de corte
  const [hh, mm] = horarioCorte.split(':').map(Number);
  const corte = new Date();
  corte.setHours(hh, mm, 0, 0);
  
  const agora = new Date();
  const antesDoCorte = agora < corte;
  
  switch (entregaUpper) {
    case 'HOJE':
      return antesDoCorte ? 'Hoje até 18h' : 'Amanhã até 12h';
    case 'AMANHÃ':
    case 'AMANHA':
      return 'Amanhã até 12h';
    case 'NOTURNA':
      return 'Amanhã até 10h';
    case 'URGENTE':
      return 'Hoje até 18:30h';
    default:
      return 'Hoje até 18h';
  }
}

/**
 * Extrai nome do arquivo de um caminho completo
 * 
 * @param filePath - Caminho completo do arquivo
 * @returns Nome do arquivo (sem diretório)
 */
export function extractFileName(filePath: string): string {
  if (!filePath || typeof filePath !== 'string') return '';
  return filePath.split('\\').pop() || '';
}

/**
 * Extrai diretório de um caminho completo
 * 
 * @param filePath - Caminho completo do arquivo
 * @returns Diretório (sem nome do arquivo)
 */
export function extractDirectory(filePath: string): string {
  if (!filePath || typeof filePath !== 'string') return '';
  if (!filePath.includes('\\')) return '';
  return filePath.substring(0, filePath.lastIndexOf('\\'));
}

/**
 * Normaliza diretório para ter barra final
 * 
 * @param directory - Diretório
 * @returns Diretório com barra final
 */
export function normalizeDirectory(directory: string): string {
  if (!directory || typeof directory !== 'string') return '';
  return directory.endsWith('\\') ? directory : `${directory}\\`;
}

